import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Library, Plus, Save, Trash2, Loader2, BookOpen, Sparkles } from "lucide-react";

const DISCIPLINES = [
  { value: "muay_thai_kickboxing", label: "Muay Thaï / Kickboxing" },
  { value: "bjj", label: "Jiu-Jitsu Brésilien" },
  { value: "jujutsu_japonais", label: "Jiu-jitsu Can Ryu" },
];

const BELTS = {
  muay_thai_kickboxing: ["Blanche", "Jaune", "Orange", "Vert", "Bleu", "Mauve", "Rouge", "Brune", "Noire"],
  bjj:             ["Blanche", "Bleue", "Mauve", "Brune", "Noire"],
  jujutsu_japonais:["Blanche", "Jaune", "Orange", "Vert", "Bleu", "Mauve", "Brune", "Noire", "Nidan"],
};

const CATEGORIES = [
  "frappe", "projection", "soumission", "clé", "immobilisation", "déplacement",
  "défense", "esquive", "balayage", "garde", "posture", "enchaînement", "transition", "autre"
];

const EMPTY_TECH = { name: "", category: "frappe", difficulty: "", description: "", objective: "", coaching_tip: "", safety_points: "", common_errors: "" };

export default function TechniquesCatalogueModule() {
  const [discipline, setDiscipline] = useState("muay_thai_kickboxing");
  const [belt, setBelt] = useState("Blanche");
  const [beltOrder, setBeltOrder] = useState(1);
  const [techniques, setTechniques] = useState([]);
  const [drafts, setDrafts] = useState([{ ...EMPTY_TECH }]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/curriculum/techniques?discipline=${discipline}&belt=${encodeURIComponent(belt)}`);
      setTechniques(data.techniques || []);
      const all = await api.get(`/curriculum/techniques?discipline=${discipline}`);
      setStats(all.data.by_discipline_belt?.[discipline] || {});
    } catch { toast.error("Erreur chargement"); }
    setLoading(false);
  }, [discipline, belt]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const idx = BELTS[discipline]?.indexOf(belt);
    if (idx >= 0) setBeltOrder(idx + 1);
  }, [belt, discipline]);

  const addDraft = () => setDrafts([...drafts, { ...EMPTY_TECH }]);
  const removeDraft = (i) => setDrafts(drafts.filter((_, idx) => idx !== i));
  const updateDraft = (i, field, value) => {
    const next = [...drafts];
    next[i][field] = value;
    setDrafts(next);
  };

  const saveAll = async () => {
    const valid = drafts.filter(d => d.name.trim());
    if (valid.length === 0) { toast.error("Saisis au moins 1 technique"); return; }
    setSaving(true);
    try {
      const payload = {
        discipline,
        belt,
        belt_order: beltOrder,
        techniques: valid.map(t => ({ ...t, discipline, belt, belt_order: beltOrder })),
      };
      const { data } = await api.post("/curriculum/techniques/bulk-import", payload);
      toast.success(`✓ ${data.total_inserted} technique(s) importée(s)${data.skipped_duplicates.length ? ` · ${data.skipped_duplicates.length} doublons ignorés` : ""}`);
      setDrafts([{ ...EMPTY_TECH }]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const removeTech = async (id) => {
    if (!window.confirm("Désactiver cette technique ?")) return;
    try {
      await api.delete(`/curriculum/techniques/${id}`);
      toast.success("Technique désactivée");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6 p-6" data-testid="techniques-catalogue-module">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Library className="w-8 h-8 text-amber-400" />
          Catalogue Techniques par Ceinture
        </h1>
        <p className="text-zinc-400 mt-1">
          Saisis ton curriculum officiel par discipline et ceinture · sert à pré-remplir les carnets élèves et à informer Camille
        </p>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <button data-testid="migrate-blanche-jaune-btn" onClick={async () => {
            if (!window.confirm("Déplacer TOUTES les techniques de ceinture Blanche vers Jaune ?\n\nLa ceinture Blanche reste le rang de départ (0 technique) ; les techniques à pratiquer pour obtenir la ceinture jaune passent sous « Jaune ». Action sûre et ré-exécutable.")) return;
            const t = toast.loading("Migration Blanche → Jaune…");
            try {
              const { data } = await api.post("/academy/admin/migrate-blanche-jaune");
              toast.dismiss(t);
              toast.success(`✅ ${data.techniques_migrees} technique(s) déplacée(s) Blanche → Jaune · ${data.carnets_recibles} carnet(s) recadré(s)`);
              load();
            } catch (e) { toast.dismiss(t); toast.error(e?.response?.data?.detail || "Migration impossible"); }
          }} className="text-xs px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 transition-colors flex items-center gap-1.5">
            <Sparkles size={13} /> Migrer Blanche → Jaune
          </button>
          <span className="text-[11px] text-zinc-500">Tout le monde débute Blanche et pratique le curriculum Jaune.</span>
        </div>
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs text-zinc-400 block">Discipline</label>
          <select
            value={discipline}
            onChange={(e) => { setDiscipline(e.target.value); setBelt(BELTS[e.target.value][0]); }}
            data-testid="discipline-selector"
            className="bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 mt-1"
          >
            {DISCIPLINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-zinc-400 block">Ceinture</label>
          <select
            value={belt}
            onChange={(e) => setBelt(e.target.value)}
            data-testid="belt-selector"
            className="bg-zinc-950 border border-zinc-700 rounded px-3 py-1.5 mt-1"
          >
            {BELTS[discipline].map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="flex-grow text-right text-sm text-zinc-400">
          Stats {DISCIPLINES.find(d => d.value === discipline)?.label} :{" "}
          {Object.entries(stats).map(([b, count]) => (
            <span key={b} className="ml-2 inline-block bg-zinc-800 px-2 py-0.5 rounded">
              {b}: <strong className="text-amber-300">{count}</strong>
            </span>
          ))}
        </div>
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
          <BookOpen className="w-5 h-5 text-amber-400" />
          Techniques saisies pour {belt} ({techniques.length})
        </h3>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
        ) : techniques.length === 0 ? (
          <p className="text-zinc-500 text-sm">Aucune technique encore. Saisis-en quelques unes ci-dessous ↓</p>
        ) : (
          <ul className="space-y-2">
            {techniques.map((t) => (
              <li key={t.id} className="flex items-start gap-3 bg-zinc-950/50 border border-zinc-800 rounded p-3" data-testid={`tech-existing-${t.id}`}>
                <div className="flex-grow">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong>{t.name}</strong>
                    <span className="text-xs px-2 py-0.5 bg-zinc-800 rounded">{t.category}</span>
                  </div>
                  {t.description && <p className="text-sm text-zinc-400 mt-1">{t.description}</p>}
                  {t.objective && <p className="text-xs text-amber-300/80 mt-1">🎯 {t.objective}</p>}
                  {t.coaching_tip && <p className="text-xs text-cyan-300/80 mt-1">💡 {t.coaching_tip}</p>}
                  {t.safety_points && <p className="text-xs text-emerald-300/80 mt-1">🛡️ {t.safety_points}</p>}
                  {t.common_errors && <p className="text-xs text-orange-300/80 mt-1">⚠️ {t.common_errors}</p>}
                </div>
                <button
                  onClick={() => removeTech(t.id)}
                  data-testid={`delete-tech-${t.id}`}
                  className="text-zinc-500 hover:text-red-400 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Plus className="w-5 h-5 text-amber-400" />
            Ajouter des techniques pour {DISCIPLINES.find(d => d.value === discipline)?.label} · {belt}
          </h3>
          <button
            onClick={addDraft}
            data-testid="add-draft-row-btn"
            className="text-amber-300 hover:text-amber-200 text-sm flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Ligne suivante
          </button>
        </div>
        <div className="space-y-3">
          {drafts.map((d, i) => (
            <div key={i} className="bg-zinc-950/40 border border-zinc-800 rounded p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-start" data-testid={`draft-row-${i}`}>
              <input
                placeholder="Nom de la technique *"
                value={d.name}
                onChange={(e) => updateDraft(i, "name", e.target.value)}
                data-testid={`draft-name-${i}`}
                className="md:col-span-3 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm"
              />
              <select value={d.category} onChange={(e) => updateDraft(i, "category", e.target.value)} data-testid={`draft-cat-${i}`} className="md:col-span-2 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input placeholder="Description courte" value={d.description} onChange={(e) => updateDraft(i, "description", e.target.value)} data-testid={`draft-desc-${i}`} className="md:col-span-6 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm" />
              <button onClick={() => removeDraft(i)} disabled={drafts.length === 1} data-testid={`draft-remove-${i}`} className="md:col-span-1 text-zinc-500 hover:text-red-400 disabled:opacity-30 p-1">
                <Trash2 className="w-4 h-4" />
              </button>
              <input placeholder="🎯 Objectif (ex: 75% maîtrise = 25 reps précis)" value={d.objective} onChange={(e) => updateDraft(i, "objective", e.target.value)} data-testid={`draft-obj-${i}`} className="md:col-span-6 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm" />
              <input placeholder="💡 Conseil / suggestion" value={d.coaching_tip} onChange={(e) => updateDraft(i, "coaching_tip", e.target.value)} data-testid={`draft-tip-${i}`} className="md:col-span-6 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm" />
              <input placeholder="🛡️ Points de sécurité" value={d.safety_points} onChange={(e) => updateDraft(i, "safety_points", e.target.value)} data-testid={`draft-safety-${i}`} className="md:col-span-6 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm" />
              <input placeholder="⚠️ Erreurs fréquentes" value={d.common_errors} onChange={(e) => updateDraft(i, "common_errors", e.target.value)} data-testid={`draft-err-${i}`} className="md:col-span-6 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm" />
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-zinc-500 max-w-xl">
            <Sparkles className="w-3 h-3 inline mr-1" />
            Astuce : commence par les techniques OBLIGATOIRES de la ceinture, ajoute les détails (objectif, conseil) plus tard. Une fois saisi, tu pourras pré-remplir automatiquement le carnet de chaque élève qui vise cette ceinture.
          </p>
          <button
            onClick={saveAll}
            disabled={saving}
            data-testid="save-all-btn"
            className="bg-amber-600 hover:bg-amber-500 text-black font-bold px-5 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer toutes les techniques
          </button>
        </div>
      </div>
    </div>
  );
}
